import { useCallback, useEffect, useRef, useState } from "react";
import { MdOutlineColorLens } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { LuImagePlus, LuPencil, LuPlus, LuTrash2, LuUpload, LuCalendar, LuInfo } from "react-icons/lu";
import { FiXCircle } from "react-icons/fi";
import { FaRegCheckCircle } from "react-icons/fa";
import { createPortal } from "react-dom";

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

/* ─────────────────────────────────────────
   Toast System
───────────────────────────────────────── */
const ToastContainer = ({ toasts, onRemove }) =>
    createPortal(
        <div className="fixed top-5 sm:right-5 mx-auto z-[100] flex flex-col gap-2.5 pointer-events-none">
            {toasts.map((t) => (
                <Toast key={t.id} toast={t} onRemove={onRemove} />
            ))}
        </div>,
        document.body
    );

const TOAST_ICONS = {
    success: <FaRegCheckCircle className="text-lg shrink-0 text-emerald-500" />,
    error: <FiXCircle className="text-lg shrink-0 text-red-500" />,
    info: <LuInfo className="text-lg shrink-0 text-blue-500" />,
};

const TOAST_STYLES = {
    success: "border-emerald-100 bg-white",
    error: "border-red-100 bg-white",
    info: "border-blue-100 bg-white",
};

const TOAST_BAR = {
    success: "bg-emerald-400",
    error: "bg-red-400",
    info: "bg-blue-400",
};

const Toast = ({ toast, onRemove }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Animate in
        const inTimer = setTimeout(() => setVisible(true), 10);
        // Auto-dismiss
        const outTimer = setTimeout(() => {
            setVisible(false);
            setTimeout(() => onRemove(toast.id), 300);
        }, toast.duration ?? 3500);
        return () => { clearTimeout(inTimer); clearTimeout(outTimer); };
    }, [toast, onRemove]);

    return (
        <div
            className={`pointer-events-auto relative flex items-start gap-3 px-4 py-3.5 rounded-xl border shadow-lg w-[340px] ml-2 overflow-hidden transition-all duration-300 ${TOAST_STYLES[toast.type]} ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}
        >
            {TOAST_ICONS[toast.type]}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#14151A]">{toast.title}</p>
                {toast.message && <p className="text-xs text-[#0F132466] mt-0.5 leading-relaxed">{toast.message}</p>}
            </div>
            <button onClick={() => { setVisible(false); setTimeout(() => onRemove(toast.id), 300); }} className="text-[#0F132440] hover:text-[#0F1324] mt-0.5">
                <IoClose />
            </button>
            {/* Progress bar */}
            <div className={`absolute bottom-0 left-0 h-0.5 ${TOAST_BAR[toast.type]} animate-toast-bar`}
                style={{ animationDuration: `${toast.duration ?? 3500}ms` }} />
        </div>
    );
};

let toastIdCounter = 0;
const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const add = useCallback((type, title, message, duration) => {
        const id = ++toastIdCounter;
        setToasts((p) => [...p, { id, type, title, message, duration }]);
    }, []);
    const remove = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
    return { toasts, remove, success: (t, m) => add("success", t, m), error: (t, m) => add("error", t, m), info: (t, m) => add("info", t, m) };
};

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* ─────────────────────────────────────────
   Main Themes Component
───────────────────────────────────────── */
const Themes = () => {
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const toast = useToast();

    useEffect(() => {
        const token = localStorage.getItem("PhilomenaAdminToken");
        const fetchThemes = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/themes`, {
                    method: "GET",
                    headers: { Authorization: `Token ${token}` },
                });
                const data = await res.json();
                setThemes(data);
            } catch (err) {
                console.error(err);
                setError("Failed to fetch themes. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchThemes();
    }, []);

    const openAdd = () => { setEditTarget(null); setModalOpen(true); };
    const openEdit = (theme) => { setEditTarget(theme); setModalOpen(true); };
    const closeModal = () => { setModalOpen(false); setEditTarget(null); };

    const handleSaved = (_, isNew) => {
        closeModal();
        toast.success(
            isNew ? "Theme added!" : "Theme updated!",
            isNew ? "Your new theme has been created successfully." : "Theme changes have been saved."
        );
        setTimeout(() => window.location.reload(), 1200);
    };

    const handleSaveError = (msg, isNew) => {
        toast.error(
            isNew ? "Failed to add theme" : "Failed to update theme",
            msg || "Something went wrong. Please try again."
        );
    };

    const handleDelete = (theme) => setDeleteTarget(theme);

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        const token = localStorage.getItem("PhilomenaAdminToken");
        setDeleting(true);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/themes/${deleteTarget.id}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Token ${token}` }
                }
            );

            // ✅ Handle 204 (No Content)
            if (res.status === 204) {
                setDeleteTarget(null);
                toast.success(
                    "Theme deleted",
                    `"${deleteTarget.name || "Untitled"}" has been removed.`
                );

                setTimeout(() => window.location.reload(), 1200);
                return;
            }

            // ✅ Only parse JSON if NOT 204
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data?.detail || "Failed to delete theme.");
            }

            setDeleteTarget(null);
            toast.success(
                "Theme deleted",
                `"${deleteTarget.name || "Untitled"}" has been removed.`
            );

            setTimeout(() => window.location.reload(), 1200);

        } catch (err) {
            console.error(err);
            toast.error(
                "Delete failed",
                err.message || "Could not delete the theme."
            );
        } finally {
            setDeleting(false);
        }
    };

    const handleToggle = async (theme) => {
        const token = localStorage.getItem("PhilomenaAdminToken");
        setThemes((prev) =>
            prev.map((t) => (t.id === theme.id ? { ...t, is_active: !t.is_active } : t))
        );
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/themes/${theme.id}/toggle-active`,
                { method: "POST", headers: { Authorization: `Token ${token}` } }
            );
            if (!res.ok) throw new Error("Toggle failed");
            const next = !theme.is_active;
            toast.success(
                next ? "Theme activated" : "Theme deactivated",
                `"${theme.name || "Untitled"}" is now ${next ? "visible to users" : "hidden from users"}.`
            );
            setTimeout(() => window.location.reload(), 1200);
        } catch (err) {
            console.error(err);
            toast.error("Toggle failed", "Could not update the theme status.");
            setThemes((prev) =>
                prev.map((t) => (t.id === theme.id ? { ...t, is_active: theme.is_active } : t))
            );
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-[90vh] gap-3">
                <div className="animate-spin rounded-full h-9 w-9 border-[3px] border-[#E9E4F5] border-t-[#62259A]" />
                <span className="text-sm text-[#0F132466]">Loading themes…</span>
            </div>
        );
    }

    if (error) return <p className="sm:py-10 py-5 text-red-600">{error}</p>;

    return (
        <>
            <ToastContainer toasts={toast.toasts} onRemove={toast.remove} />

            <div className="sm:py-10 py-5 space-y-8 overflow-x-hidden">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-semibold text-2xl text-[#14151A]">Themes</p>
                        <p className="text-sm text-[#0F132466] mt-0.5">
                            {themes.length} theme{themes.length !== 1 ? "s" : ""} total
                        </p>
                    </div>
                    <button
                        onClick={openAdd}
                        className="flex items-center gap-2 bg-[#62259A] text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-[#4e1d80] active:scale-95 transition-all shadow-sm shadow-[#62259A33]"
                    >
                        <LuPlus className="text-base" />
                        Add theme
                    </button>
                </div>

                {/* Empty state */}
                {themes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[360px] border-2 border-dashed border-[#D4BBF0] rounded-2xl gap-4 bg-[#62259A04]">
                        <div className="bg-[#62259A12] rounded-2xl text-[#62259A] text-4xl size-20 flex items-center justify-center">
                            <MdOutlineColorLens />
                        </div>
                        <div className="text-center space-y-1.5">
                            <p className="font-semibold text-[#14151A] text-base">No themes yet</p>
                            <p className="text-sm text-[#0F132466]">Add your first theme to get started</p>
                        </div>
                        <button
                            onClick={openAdd}
                            className="mt-1 flex items-center gap-2 bg-[#62259A] text-white text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-[#4e1d80] transition-all shadow-sm shadow-[#62259A33]"
                        >
                            <LuPlus /> Add theme
                        </button>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-5">
                        {themes.map((theme) => (
                            <ThemeCard
                                key={theme.id}
                                theme={theme}
                                onEdit={() => openEdit(theme)}
                                onDelete={handleDelete}
                                onToggle={handleToggle}
                            />
                        ))}
                    </div>
                )}
            </div>

            {modalOpen && (
                <ThemeModal
                    theme={editTarget}
                    onClose={closeModal}
                    onSaved={handleSaved}
                    onError={handleSaveError}
                />
            )}

            {deleteTarget && (
                <DeleteConfirmationModal
                    theme={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={confirmDelete}
                    loading={deleting}
                />
            )}

            <style>{`
                @keyframes toast-bar {
                    from { width: 100%; }
                    to   { width: 0%; }
                }
                .animate-toast-bar { animation: toast-bar linear forwards; }
            `}</style>
        </>
    );
};

/* ─────────────────────────────────────────
   Theme Card
───────────────────────────────────────── */
const ThemeCard = ({ theme, onEdit, onDelete, onToggle }) => (
    <div className="border-[1.5px] border-[#EBECEC] rounded-2xl bg-white overflow-hidden group hover:border-[#C4A3E0] hover:shadow-md transition-all duration-200">

        {/* Image */}
        <div className="w-full h-44 bg-gradient-to-br from-[#F3F0F7] to-[#EDE8F5] relative overflow-hidden">
            {theme.landing_image ? (
                <img
                    src={theme.landing_image}
                    alt={theme.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
            ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-[#0F132433]">
                    <MdOutlineColorLens className="text-3xl" />
                    <span className="text-xs">No image</span>
                </div>
            )}

            {/* Active badge */}
            <button
                onClick={() => onToggle(theme)}
                title={theme.is_active ? "Click to deactivate" : "Click to activate"}
                className={`absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full transition-all cursor-pointer select-none backdrop-blur-sm ${theme.is_active
                    ? "bg-emerald-100/90 text-emerald-700 border border-emerald-200"
                    : "bg-white/80 text-[#0F132466] border border-[#EBECEC]"
                    }`}
            >
                <span className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${theme.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
                    {theme.is_active ? "Active" : "Inactive"}
                </span>
            </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
            <div>
                <p className="font-semibold text-[#14151A] text-[18px] truncate">
                    {theme.name || <span className="text-[#0F132433] font-normal italic">Untitled theme</span>}
                </p>
                <p className="text-base text-[#0F132466] mt-0.5 line-clamp-2 leading-relaxed">
                    {theme.description || <span className="italic">No description</span>}
                </p>
            </div>

            {/* Date added */}
            <div className="flex items-center gap-1.5 text-[14px] text-[#0F132440]">
                <LuCalendar className="shrink-0" />
                <span>Added {formatDate(theme.date_created)}</span>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-0.5">
                <button
                    onClick={onEdit}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-[#F3F0F7] text-[#62259A] text-sm font-medium py-2.5 rounded-xl hover:bg-[#E9E4F5] active:scale-95 transition-all"
                >
                    <LuPencil className="text-sm" /> Edit
                </button>
                <button
                    onClick={() => onDelete(theme)}
                    title="Delete theme"
                    className="flex items-center justify-center w-10 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 active:scale-95 transition-all"
                >
                    <LuTrash2 className="text-sm" />
                </button>
            </div>
        </div>
    </div>
);

/* ─────────────────────────────────────────
   Delete Confirmation Modal
───────────────────────────────────────── */
const DeleteConfirmationModal = ({ theme, onClose, onConfirm, loading }) =>
    createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-modal">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-500 to-rose-500 px-6 py-5 flex items-center justify-between">
                    <div>
                        <p className="text-white font-semibold text-lg">Delete theme</p>
                        <p className="text-white/70 text-xs mt-0.5">This action cannot be undone</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition-colors">
                        <IoClose className="text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-4">
                    <p className="text-sm text-[#14151A]">
                        Are you sure you want to delete{" "}
                        <span className="font-semibold">"{theme.name || "Untitled"}"</span>?
                    </p>
                    <div className="bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl px-4 py-3">
                        This will permanently remove this theme and all its data.
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-[#FAFAFA] border-t flex gap-3">
                    <button onClick={onClose} disabled={loading} className="flex-1 bg-white border-[1.5px] border-[#EBECEC] text-sm font-medium py-2.5 rounded-xl hover:bg-[#F7F7F8] transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-red-500 to-rose-500 text-white text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 hover:opacity-90 transition-all active:scale-[0.98]"
                    >
                        {loading ? (
                            <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" /> Deleting…</>
                        ) : (
                            <><LuTrash2 /> Delete theme</>
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes modal-in {
                    from { opacity: 0; transform: translateY(16px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-modal { animation: modal-in 0.22s cubic-bezier(0.16,1,0.3,1) both; }
            `}</style>
        </div>,
        document.body
    );

/* ─────────────────────────────────────────
   Theme Modal  (Add & Edit)
───────────────────────────────────────── */
const ThemeModal = ({ theme, onClose, onSaved, onError }) => {
    const isEdit = !!theme;
    const fileRef = useRef(null);

    const [form, setForm] = useState({
        name: theme?.name ?? "",
        description: theme?.description ?? "",
        is_active: theme?.is_active ?? true,
    });

    const [imageUrl, setImageUrl] = useState(theme?.landing_image ?? "");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(theme?.landing_image ?? "");
    const [dragOver, setDragOver] = useState(false);

    const [uploadProgress, setUploadProgress] = useState({});
    const [isUploadingLanding, setIsUploadingLanding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const uploadToCloudinaryWithProgress = useCallback((file, progressKey) => {
        return new Promise((resolve, reject) => {
            if (!CLOUD_NAME || !UPLOAD_PRESET) { reject(new Error("Cloudinary not configured.")); return; }
            const url = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/upload`;
            const data = new FormData();
            data.append("file", file);
            data.append("upload_preset", UPLOAD_PRESET);
            const xhr = new XMLHttpRequest();
            xhr.open("POST", url);
            xhr.upload.onprogress = (e) => {
                if (!e.lengthComputable) return;
                setUploadProgress((prev) => ({ ...prev, [progressKey]: Math.round((e.loaded / e.total) * 100) }));
            };
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const json = JSON.parse(xhr.responseText);
                            setUploadProgress((prev) => ({ ...prev, [progressKey]: 100 }));
                            setTimeout(() => setUploadProgress((prev) => { const n = { ...prev }; delete n[progressKey]; return n; }), 1200);
                            resolve(json.secure_url);
                        } catch (err) { reject(err); }
                    } else {
                        let message = "Upload failed";
                        try { const p = JSON.parse(xhr.responseText || "{}"); if (p?.error?.message) message = p.error.message; } catch { }
                        if (/whitelisted for unsigned uploads/i.test(message)) message = "Cloudinary preset not allowed for unsigned uploads.";
                        reject(new Error(message));
                    }
                }
            };
            xhr.onerror = () => reject(new Error("Network error during upload"));
            xhr.send(data);
        });
    }, []);

    const handleFile = (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        setImageUrl("");
    };

    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };

    const removeImage = () => {
        setImageFile(null); setImagePreview(""); setImageUrl("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleSave = async () => {
        const token = localStorage.getItem("PhilomenaAdminToken");
        setSaving(true);
        setSaveError(null);
        try {
            let finalImageUrl = imageUrl;
            if (imageFile) {
                setIsUploadingLanding(true);
                try { finalImageUrl = await uploadToCloudinaryWithProgress(imageFile, "landing_image"); }
                finally { setIsUploadingLanding(false); }
            }

            const endpoint = isEdit
                ? `${process.env.REACT_APP_BACKEND_URL}/themes/${theme.id}`
                : `${process.env.REACT_APP_BACKEND_URL}/themes`;

            const res = await fetch(endpoint, {
                method: isEdit ? "PUT" : "POST",
                headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
                body: JSON.stringify({ name: form.name, description: form.description, landing_image: finalImageUrl, is_active: form.is_active }),
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData?.detail || "Failed to save theme.");
            }
            const saved = await res.json();
            onSaved({ id: saved.id ?? theme?.id ?? Date.now(), name: form.name, description: form.description, imageUrl: finalImageUrl, is_active: form.is_active }, !isEdit);
        } catch (err) {
            console.error(err);
            const msg = err.message || "Could not save. Please try again.";
            setSaveError(msg);
            onError(msg, !isEdit);
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-[2px]"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-modal">

                {/* Header */}
                <div className="bg-gradient-to-r from-[#62259A] to-[#8B3FC8] px-6 py-5 flex items-center justify-between">
                    <div>
                        <p className="text-white font-semibold text-lg leading-tight">
                            {isEdit ? "Edit theme" : "Add new theme"}
                        </p>
                        <p className="text-white/60 text-xs mt-0.5">
                            {isEdit ? `Editing "${theme.name || "Untitled"}"` : "Fill in the details below"}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition-colors">
                        <IoClose className="text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto">

                    {/* Image upload */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-[#0F132499]">Theme image</label>

                        {imagePreview ? (
                            <div className="relative rounded-xl overflow-hidden border-[1.5px] border-[#EBECEC] group">
                                <img src={imagePreview} alt="Preview" className="w-full h-52 object-cover object-top" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-all duration-200 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                                    <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 bg-white text-[#62259A] text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#F3F0F7]">
                                        <LuUpload className="text-sm" /> Change
                                    </button>
                                    <button onClick={removeImage} className="flex items-center gap-1.5 bg-white text-red-500 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-red-50">
                                        <LuTrash2 className="text-sm" /> Remove
                                    </button>
                                </div>
                                <div className="absolute bottom-3 left-3 bg-black/50 text-white text-[11px] px-2 py-0.5 rounded-md backdrop-blur-sm">
                                    {imageFile ? imageFile.name : "Current image"}
                                </div>
                            </div>
                        ) : (
                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                onClick={() => fileRef.current?.click()}
                                className={`flex flex-col items-center justify-center gap-3 h-44 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${dragOver ? "border-[#62259A] bg-[#62259A0A]" : "border-[#D4BBF0] bg-[#62259A04] hover:border-[#62259A] hover:bg-[#62259A08]"}`}
                            >
                                <div className="bg-[#62259A12] rounded-full p-3 text-[#62259A] text-2xl">
                                    <LuImagePlus />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-medium text-[#14151A]">Drop image here or <span className="text-[#62259A]">browse</span></p>
                                    <p className="text-xs text-[#0F132466] mt-0.5">PNG, JPG, WEBP up to 5MB</p>
                                </div>
                            </div>
                        )}

                        {isUploadingLanding && (
                            <div className="space-y-1.5">
                                <div className="flex justify-between text-xs text-[#0F132466]">
                                    <span>Uploading image…</span>
                                    <span>{uploadProgress.landing_image ?? 0}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-[#F3F0F7] rounded-full overflow-hidden">
                                    <div className="h-full bg-[#62259A] transition-all duration-200" style={{ width: `${uploadProgress.landing_image ?? 0}%` }} />
                                </div>
                            </div>
                        )}

                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-[#EBECEC]" />
                        <span className="text-xs text-[#0F132433] font-medium uppercase tracking-wide">Details</span>
                        <div className="flex-1 h-px bg-[#EBECEC]" />
                    </div>

                    {/* Name */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-[#0F132499]">Theme name <span className="text-red-400">*</span></label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            placeholder="e.g. Blush Rose"
                            className="w-full border-[1.5px] border-[#EBECEC] focus:border-[#62259A] rounded-xl px-3.5 py-2.5 text-sm text-[#14151A] outline-none transition-colors placeholder:text-[#0F132433]"
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-[#0F132499]">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Describe this theme…"
                            rows={3}
                            className="w-full border-[1.5px] border-[#EBECEC] focus:border-[#62259A] rounded-xl px-3.5 py-2.5 text-sm text-[#14151A] outline-none transition-colors resize-none placeholder:text-[#0F132433]"
                        />
                    </div>

                    {/* Active toggle */}
                    <div className="flex items-center justify-between bg-[#FAFAFA] border-[1.5px] border-[#EBECEC] rounded-xl px-4 py-3.5">
                        <div>
                            <p className="text-sm font-medium text-[#14151A]">Active</p>
                            <p className="text-xs text-[#0F132466] mt-0.5">Visible to users on the platform</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${form.is_active ? "bg-[#62259A]" : "bg-[#D1D5DB]"}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${form.is_active ? "translate-x-5" : "translate-x-0"}`} />
                        </button>
                    </div>

                    {saveError && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                            {saveError}
                        </p>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-[#FAFAFA] border-t border-[#EBECEC] flex items-center gap-3">
                    <button onClick={onClose} className="flex-1 bg-white border-[1.5px] border-[#EBECEC] text-[#14151A] text-sm font-medium py-2.5 rounded-xl hover:bg-[#F7F7F8] transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={
                            saving ||
                            !form.name.trim() ||
                            !imagePreview ||
                            isUploadingLanding ||
                            !form.description.trim()
                        }
                        className="flex-1 bg-gradient-to-r from-[#62259A] to-[#8B3FC8] text-white text-sm font-medium py-2.5 rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm shadow-[#62259A33]"
                    >
                        {saving ? (
                            <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" /> Saving…</>
                        ) : (
                            isEdit ? "Save changes" : "Add theme"
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes modal-in {
                    from { opacity: 0; transform: translateY(16px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-modal { animation: modal-in 0.22s cubic-bezier(0.16,1,0.3,1) both; }
            `}</style>
        </div>,
        document.body
    );
};

export default Themes;